import { DistanceUnitEnum } from "@kavval/constants";
import type { Locale } from "@kavval/lingui-config/locales";
import type { Maybe } from "@kavval/ts-utils";

import { metersToMiles } from "../converters";

const format = ({
  distance,
  unit,
  maximumFractionDigits,
  locale,
  shouldDisplayUnit,
}: {
  distance: number;
  unit: string;
  maximumFractionDigits?: number;
  locale: Locale;
  shouldDisplayUnit?: boolean;
}) => {
  const unitDisplayed = shouldDisplayUnit ? `\u2009${unit}` : "";

  return `${new Intl.NumberFormat(locale, { maximumFractionDigits }).format(
    distance
  )}${unitDisplayed}`;
};

/**
 * Formate une distance en fonction de la locale et de l'unité spécifiée.
 *
 * @param {Object} params - Les paramètres de formatage
 * @param {Locale} params.locale - La locale à utiliser pour le formatage
 * @param {number} params.distance - La distance en mètres à formater
 * @param {boolean} [params.shouldDisplayUnit=true] - Indique si l'unité doit être affichée
 * @param {DistanceUnitEnum} [params.unit=DistanceUnitEnum.Kilometers] - L'unité de distance à utiliser
 * @returns {string} La distance formatée
 *
 * @example
 * // Retourne "1.5 km"
 * formatDistance({ locale: 'fr', distance: 1500 })
 *
 * @example
 * // Retourne "0.93 mi"
 * formatDistance({ locale: 'en', distance: 1500, unit: DistanceUnitEnum.Miles })
 */
export const formatDistance = ({
  locale,
  distance,
  shouldDisplayUnit = true,
  unit = DistanceUnitEnum.Meters,
}: {
  locale: Locale;
  distance: number;
  shouldDisplayUnit?: boolean;
  unit?: Maybe<DistanceUnitEnum>;
}) => {
  if (unit === DistanceUnitEnum.Miles) {
    return format({
      locale,
      shouldDisplayUnit,
      distance: metersToMiles(distance),
      unit: "mi",
      maximumFractionDigits: 2,
    });
  }
  if (distance < 1000) {
    return format({
      locale,
      shouldDisplayUnit,
      distance,
      unit: "m",
    });
  } else {
    return format({
      locale,
      shouldDisplayUnit,
      distance: distance === 21097 ? 21.0975 : distance / 1000,
      unit: "km",
      maximumFractionDigits: 4,
    });
  }
};
